"use client";

import { useEffect, useState } from "react";
import { useShouldLoadThirdPartyScripts } from "@/helpers/useShouldLoadThirdPartyScripts";
import { useModalState } from "@/context/ModalContext";
import {
  areLeadModalsForced,
  forceLeadModalsLoad,
  getLeadModalHost,
  subscribeLeadModalHost,
  subscribeLeadModalsForce,
  warmLeadModals,
} from "@/helpers/leadModalGate";
import { preloadAllModalChunks } from "./modalChunkPreload";
import ModalPageScrollLock from "./ModalPageScrollLock";

/**
 * Idle-gates CommonModals for INP, but always mounts when a lead CTA has
 * already opened the modal (same-tick safety) or intent-warmed the gate.
 *
 * Host is read from leadModalGate's registry on every render (not next/dynamic
 * loading:null). prepareLeadModalOpen() registers the host before OpenModal
 * dispatches, so the first paint includes the real ShareBanner/form body.
 */
export default function CommonModalsLazy({ isMobile }: { isMobile: boolean }) {
  const shouldLoadScripts = useShouldLoadThirdPartyScripts();
  const [forced, setForced] = useState(areLeadModalsForced);
  const { isModalOpen } = useModalState();
  // Epoch only forces a re-render when the host registers while idle (modal closed).
  const [, setHostEpoch] = useState(0);

  useEffect(() => subscribeLeadModalsForce(() => setForced(true)), []);
  useEffect(
    () => subscribeLeadModalHost(() => setHostEpoch((n) => n + 1)),
    [],
  );

  useEffect(() => {
    if (isModalOpen) forceLeadModalsLoad();
  }, [isModalOpen]);

  useEffect(() => {
    if (shouldLoadScripts || forced || isModalOpen) {
      warmLeadModals();
      preloadAllModalChunks();
    }
  }, [shouldLoadScripts, forced, isModalOpen]);

  const Host = getLeadModalHost();
  const showHost = Boolean(
    Host && (shouldLoadScripts || forced || isModalOpen),
  );

  return (
    <>
      <ModalPageScrollLock />
      {showHost && Host ? <Host isMobile={isMobile} /> : null}
    </>
  );
}
